import { Dialog, Grid } from '@mui/material';
import { Colors, PrimaryButton, Text } from 'library';
import React, { useEffect, useState } from 'react';
import ApiClient from 'shared/ApiClient';
import { track } from 'shared/Tracking';
import { useUserContext } from 'UserContext';
import { PlaidAccount } from './components/PlaidTypes';

interface AccountSelectionError {
    accountId: PlaidAccount['id'];
    message: string;
}

interface PlaidAccountSelectionProps {
    accounts?: PlaidAccount[];
    token?: string;
    onSelectAccount: (accountId: PlaidAccount['id']) => void;
    onError: () => void;
}

export default function PlaidAccountSelection({
    accounts = [],
    token,
    onSelectAccount,
    onError,
}: PlaidAccountSelectionProps) {
    const { user } = useUserContext();
    const [error, setError] = useState<AccountSelectionError | undefined>();
    const [loading, setLoading] = useState(false);
    const [selectedAccountId, setSelectedAccountId] = useState<PlaidAccount['id']>();

    useEffect(() => {
        if (accounts && accounts.length === 1) {
            selectAccount(accounts[0].id);
        }
    }, [accounts]);

    async function selectAccount(accountId: PlaidAccount['id']) {
        setSelectedAccountId(accountId);
        setLoading(true);
        setError(undefined);

        try {
            const response = await ApiClient(`plaid-user/?id=${user?.id}`, {
                body: { public_token: token, account_id: accountId },
            });
            track('Plaid Setup', { status: response });
            onSelectAccount(accountId);
        } catch (error: any) {
            onSubmissionError(accountId, error);
        }

        setLoading(false);
        setSelectedAccountId(undefined);
    }

    function onSubmissionError(accountId: PlaidAccount['id'], error: { message: string }) {
        setError({
            accountId,
            message: error.message || 'There was an error adding the payment method',
        });
        track('Plaid Error', { error });
        ApiClient('payment/api/setup-events/', { body: { data: error, processor: 'Plaid' } });
        setTimeout(onError, 5000);
    }

    return (
        <Dialog open={!!accounts && accounts.length > 0}>
            {accounts.map((account) => (
                <Account
                    key={account.id}
                    onSelected={selectAccount}
                    item={account}
                    error={error?.accountId === account.id ? error : undefined}
                    loading={loading && selectedAccountId === account.id}
                    disabled={loading}
                />
            ))}
        </Dialog>
    );
}

function Account({
    item,
    error,
    loading,
    disabled,
    onSelected,
}: {
    item: PlaidAccount;
    error?: AccountSelectionError;
    loading: boolean;
    disabled: boolean;
    onSelected: (id: PlaidAccount['id']) => void;
}) {
    return (
        <Grid container direction="row" style={{ padding: '1em', borderBottom: `1px solid ${Colors.darkNavy}` }}>
            <Grid container xs={8} alignItems="center">
                <Text>
                    {item.name} - {item.mask}
                </Text>
                {error && error.accountId === item.id && <Text>{error.message}</Text>}
            </Grid>
            <Grid container xs={4} justifyContent="center">
                <PrimaryButton
                    disabled={disabled}
                    loading={loading}
                    onClick={() => onSelected(item.id)}
                    buttonStyle={{ width: '100%', height: 35 }}
                >
                    Select
                </PrimaryButton>
            </Grid>
        </Grid>
    );
}
